/**
 * Call Follow-Up Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface for call follow-up operations.
 * Similar to Flutter's repository implementation pattern.
 *
 * This layer is responsible for:
 * - Coordinating datasources
 * - Error handling and transformation
 * - Caching strategies (if needed)
 * - Data mapping between layers
 */

import { AppException, NotFoundException } from '@/core/exceptions';

import type { CallFollowUpRepository } from '../../domain/repositories/callFollowUpRepository';
import type { CallFollowUpSearchRequest } from '../../domain/types/requests';
import type { CallFollowUpSearchResponse, CallFollowUpSearchOptionsResponse } from '../../domain/types/responses';
import type { CallFollowUpDatasource } from '../datasources/callFollowUpDatasource';
import { callFollowUpDatasource } from '../datasources/callFollowUpDatasource';

// ============================================================================
// Repository Implementation
// ============================================================================

export class CallFollowUpRepositoryImpl implements CallFollowUpRepository {
  constructor(private readonly datasource: CallFollowUpDatasource = callFollowUpDatasource) {}

  async searchCallFollowUps(request: CallFollowUpSearchRequest): Promise<CallFollowUpSearchResponse> {
    try {
      return await this.datasource.searchCallFollowUps(request);
    } catch (error) {
      if (error instanceof NotFoundException) {
        return { items: [], total: 0, page: request.page, per_page: request.per_page };
      }
      throw this.handleError(error, 'Failed to search call follow-ups');
    }
  }

  async getSearchOptions(): Promise<CallFollowUpSearchOptionsResponse> {
    try {
      return await this.datasource.getSearchOptions();
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch search options');
    }
  }

  /**
   * Transform errors to domain exceptions
   */
  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }

    if (error instanceof Error) {
      return new AppException(error.message);
    }

    return new AppException(fallbackMessage);
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const callFollowUpRepository: CallFollowUpRepository = new CallFollowUpRepositoryImpl();
